import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faInbox,
    faTruck,
    faCheckCircle,
    faFlask,
    faDatabase,
    faCalendarAlt,
    faPlus,
    faMinus,
    faDna,
    faBacteria,
    faTooth,
    faCircleNodes,
    faAtom,
    faOilCan,
    faFlaskVial,
    faDroplet
} from '@fortawesome/free-solid-svg-icons';
import SampleDetail from './SampleDetail';
import './Calendar.css';

const Calendar = ({ onDateSelect, onSearch, dateFilter, searchTerm, refreshTrigger, onRefreshData }) => {
    const [samples, setSamples] = useState([]);
    const [filteredSamples, setFilteredSamples] = useState([]);
    const [groupedSamples, setGroupedSamples] = useState([]);
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
    const [selectedDate, setSelectedDate] = useState(dateFilter);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [selectedSample, setSelectedSample] = useState(null);
    const [showDetailCard, setShowDetailCard] = useState(false);
    const [detailCardPosition, setDetailCardPosition] = useState({ x: 0, y: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const calendarRef = useRef(null);
    const todayRef = useRef(null);
    
    // Status icons mapping
    const statusIcons = {
        'Order Received': faInbox,
        'Sent Cargo': faTruck,
        'Cargo Delivered': faCheckCircle,
        'Lab Received': faFlask,
        'Data Delivered': faDatabase
    };
    const kitTypes = {
        'Lifelong Health Test': faDna,
        'Gut Health Test': faBacteria,
        'Oral Health Test': faTooth,

    };
    
    // Status colors mapping

    // Load sample data from API
    const fetchSamples = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch('https://service1.szapfs.org/reqvalidorders');
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Transform the data to match our application's structure
            const transformedData = data.map(order => ({
                patientId: order.patientId || `PAT-${order.barcode.substring(3)}`,
                barcode: order.barcode,
                firstName: order.firstName,
                lastName: order.lastName,
                address: order.address,
                kitType: order.kitType,
                status: {
                    currentStage: order.currentStage,
                    stages: {
                        "Order Received": order.order_received,
                        "Sent Cargo": order.sent_cargo,
                        "Cargo Delivered": order.cargo_delivered,
                        "Lab Received": order.lab_received,
                        "Data Delivered": order.data_delivered
                    }
                }
            }));
            
            // Filter out samples that have already been delivered
            const loadedSamples = transformedData.filter(patient => {
                return patient.status.currentStage == patient.status.currentStage ;
            });
            
            setSamples(loadedSamples);
            setFilteredSamples(loadedSamples);
            setError(null);
        } catch (err) {
            console.error("Error fetching samples for calendar:", err);
            setError("Failed to load samples. Please try again later.");
            setSamples([]);
            setFilteredSamples([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial data fetch
    useEffect(() => {
        fetchSamples();
    }, [fetchSamples]);
    
    // Refresh data when refreshTrigger changes
    useEffect(() => {
        if (refreshTrigger > 0) {
            console.log('Calendar refreshing data due to trigger:', refreshTrigger);
            fetchSamples();
        }
    }, [refreshTrigger, fetchSamples]);

    // Scroll to today when component mounts or when zoom level changes
    useEffect(() => {
        if (!todayRef.current || !calendarRef.current) return;
        const container = calendarRef.current;
        const dayEl = todayRef.current;
        
        // Day center in original (unscaled) coordinates
        const dayCenter = dayEl.offsetLeft + (dayEl.offsetWidth / 2);
        
        // Desired new scrollLeft so that dayCenter remains in the middle after zoom
        const newScrollLeft = (dayCenter * zoomLevel) - (container.clientWidth / 2);
        
        // Clamp scroll to valid range
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.min(Math.max(newScrollLeft, 0), maxScrollLeft);
    }, [zoomLevel]);

    // Filter samples based on search term
    useEffect(() => {
        if (localSearchTerm.trim() === '') {
            setFilteredSamples(samples);
        } else {
            const filtered = samples.filter(sample => {
                const searchTermLower = localSearchTerm.toLowerCase();
                return (
                    (sample.patientId && sample.patientId.toLowerCase().includes(searchTermLower)) ||
                    sample.barcode.toLowerCase().includes(searchTermLower) ||
                    sample.firstName.toLowerCase().includes(searchTermLower) ||
                    sample.lastName.toLowerCase().includes(searchTermLower) ||
                    sample.kitType.toLowerCase().includes(searchTermLower)
                );
            });
            setFilteredSamples(filtered);
        }
    }, [localSearchTerm, samples]);

    // Group samples into rows to avoid collisions
    useEffect(() => {
        const days = generateDays();
        const samplesWithBars = filteredSamples
            .map(sample => {
                const barData = calculateSampleBar(sample);
                if (!barData) return null;

                // Find the indices in the days array by comparing timestamps
                const startIndex = days.findIndex(day => {
                    const dayTime = new Date(day);
                    dayTime.setHours(0, 0, 0, 0);
                    return dayTime.getTime() === barData.startDate.getTime();
                });
                
                const endIndex = days.findIndex(day => {
                    const dayTime = new Date(day);
                    dayTime.setHours(0, 0, 0, 0);
                    return dayTime.getTime() === barData.endDate.getTime();
                });
                
                if (startIndex === -1) return null;
                
                // Ensure at least 1 day width and handle case where endIndex is not found
                const duration = (endIndex !== -1 ? Math.max(1, endIndex - startIndex + 1) : 1);
                
                return {
                    sample,
                    startIndex,
                    endIndex: startIndex + duration - 1,
                    barData
                };
            })
            .filter(Boolean); // Remove nulls

        // Sort samples by start date, then by duration (shorter ones first)
        samplesWithBars.sort((a, b) => {
            // First by start date
            if (a.startIndex !== b.startIndex) {
                return a.startIndex - b.startIndex;
            }
            // Then by duration (shorter first)
            return (a.endIndex - a.startIndex) - (b.endIndex - b.startIndex);
        });

        // Group samples into rows to avoid collisions
        const rows = [];
        
        samplesWithBars.forEach(sampleWithBar => {
            // Find first row where this sample can fit without overlap
            let rowIndex = 0;
            let placed = false;
            
            while (!placed) {
                if (!rows[rowIndex]) {
                    // Create a new row if it doesn't exist
                    rows[rowIndex] = [];
                    rows[rowIndex].push(sampleWithBar);
                    placed = true;
                } else {
                    // Check if sample overlaps with any sample in the current row
                    const overlaps = rows[rowIndex].some(existingSample => {
                        // Check for overlap
                        return (
                            (sampleWithBar.startIndex <= existingSample.endIndex) && 
                            (sampleWithBar.endIndex >= existingSample.startIndex)
                        );
                    });
                    
                    if (!overlaps) {
                        // If no overlap, place in this row
                        rows[rowIndex].push(sampleWithBar);
                        placed = true;
                    } else {
                        // Try next row
                        rowIndex++;
                    }
                }
            }
        });
        
        // Set grouped samples
        setGroupedSamples(rows);
    }, [filteredSamples]);

    // Calculate date range for the calendar - show 1.5 months in each direction
    const getDateRange = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Create date 1.5 months before today
        const startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1); // First go back 1 month
        startDate.setDate(today.getDate() - 15); // Then go back 15 days (approx half month)
        
        // Create date 1.5 months after today
        const endDate = new Date(today);
        endDate.setMonth(today.getMonth() + 1); // First go forward 1 month
        endDate.setDate(today.getDate() + 15); // Then go forward 15 days (approx half month)
        
        return { startDate, endDate };
    };

    // Generate days for the calendar
    const generateDays = () => {
        const { startDate, endDate } = getDateRange();
        const days = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    };

    // Check if a date is today
    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    // Calculate sample bar position and width
    const calculateSampleBar = (sample) => {
        const stages = sample.status.stages;
        let startDate = null;
        let endDate = null;
        const stagesDates = {};

        // Find the earliest and latest dates
        Object.entries(stages).forEach(([stage, date]) => {
            if (date) {
                const stageDate = new Date(date);
                stageDate.setHours(0, 0, 0, 0); // Normalize to start of day
                if (!startDate || stageDate < startDate) startDate = stageDate;
                if (!endDate || stageDate > endDate) endDate = stageDate;
                stagesDates[stage] = stageDate; // Store each stage's date
            }
        });

        if (!startDate) return null;

        // Calculate progress percentage
        const stageOrder = ['Order Received', 'Sent Cargo', 'Cargo Delivered', 'Lab Received', 'Data Delivered'];
        const currentStageIndex = stageOrder.indexOf(sample.status.currentStage);
        const progressPercentage = Math.round((currentStageIndex / (stageOrder.length - 1)) * 100);

        return {
            startDate,
            endDate: endDate || new Date(),
            progressPercentage,
            stagesDates // Include all stage dates
        };
    };

    // Handle zoom in
    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.2, 2));
    };

    // Handle zoom out
    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
    };

    // Handle sample click to show detail card
    const handleSampleClick = (e, sample) => {
        console.log('Calendar: Sample clicked', sample.barcode);
        setSelectedSample(sample);
        setShowDetailCard(true);
    };

    // Close detail card
    const handleCloseDetailCard = () => {
        console.log('Calendar: Closing detail card');
        setShowDetailCard(false);
        setSelectedSample(null);
    };

    // Get current month and year
    const getCurrentMonthYear = () => {
        const now = new Date();
        return `${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`;
    };

    // Handle search change
    const handleSearchChange = (e) => {
        const newSearchTerm = e.target.value;
        setLocalSearchTerm(newSearchTerm);
        if (onSearch) {
            onSearch(newSearchTerm);
        }
    };

    // Handle day click
    const handleDayClick = (date) => {
        console.log('Calendar: Day clicked', date);
        
        // Normalize dates for comparison by setting time to 00:00:00
        const normalizedClickedDate = new Date(date);
        normalizedClickedDate.setHours(0, 0, 0, 0);
        
        let newSelectedDate = null;
        
        // Check if there's already a selected date
        if (selectedDate) {
            const normalizedSelectedDate = new Date(selectedDate);
            normalizedSelectedDate.setHours(0, 0, 0, 0);
            
            // If the clicked date is the same as the selected date, unselect it
            if (normalizedClickedDate.getTime() === normalizedSelectedDate.getTime()) {
                console.log('Calendar: Unselecting date', date);
                newSelectedDate = null;
            } else {
                // Otherwise, select the new date
                console.log('Calendar: Selecting new date', date);
                newSelectedDate = date;
            }
        } else {
            // No date was selected before, so select this one
            console.log('Calendar: Setting initial date', date);
            newSelectedDate = date;
        }
        
        // Update local state
        setSelectedDate(newSelectedDate);
        
        // Notify parent component
        if (onDateSelect) {
            console.log('Calendar: Calling onDateSelect with', newSelectedDate);
            onDateSelect(newSelectedDate);
        }
    };

    // Render the calendar
    return (
        <div className="samplemanager-calendar-container">
            <div className="samplemanager-calendar-container-top">
                <div className="samplemanager-calendar-header-left">
                    <FontAwesomeIcon icon={faCalendarAlt} className="samplemanager-calendar-icon" />
                    <span className="samplemanager-calendar-month">{getCurrentMonthYear()}</span>
                </div>
                <div className="samplemanager-calendar-header-right">
                    <div className="samplemanager-calendar-search">
                        <FontAwesomeIcon icon={faSearch} className="samplemanager-search-icon" />
                        <input 
                            type="text"
                            value={localSearchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search samples..."
                            className="samplemanager-search-input"
                        />
                    </div>
                </div>
            </div>
            <div className="samplemanager-calendar-container-bottom" ref={calendarRef}>
                <div 
                    className="samplemanager-calendar-grid" 
                    style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'left top' }}
                >
                    <div className="samplemanager-calendar-days">
                        {generateDays().map((day, index) => {
                            const dayIsToday = isToday(day);
                            const date = new Date(day);
                            date.setHours(0, 0, 0, 0);
                            const isSelected = selectedDate && new Date(selectedDate).setHours(0, 0, 0, 0) === date.getTime();
                            return (
                                <div 
                                    key={index} 
                                    className={`samplemanager-calendar-day ${dayIsToday ? 'samplemanager-today' : ''} ${isSelected ? 'selected' : ''}`}
                                    ref={dayIsToday ? todayRef : null}
                                    onClick={() => handleDayClick(day)}
                                >
                                    <div className="samplemanager-day-header">
                                        <span className="samplemanager-day-name">{day.toLocaleString('default', { weekday: 'short' })}</span>
                                        <span className="samplemanager-day-number">{day.getDate()}</span>
                                    </div>
                                    <div className="samplemanager-day-vertical-line-right"></div>
                                    {dayIsToday && <div className="samplemanager-today-vertical-line"></div>}
                                </div>
                            );
                        })}
                    </div>
                    <div className="samplemanager-calendar-samples">
                        {groupedSamples.map((row, rowIndex) => (
                            <div 
                                key={`row-${rowIndex}`} 
                                className="samplemanager-sample-row"
                                style={{ top: `${rowIndex * 3}vh` }}
                            >
                                {row.map((sampleWithBar, sampleIndex) => {
                                    const { sample, startIndex, endIndex, barData } = sampleWithBar;
                                    const dayWidth = 10; // 10vh per day
                                    const barWidth = (endIndex - startIndex + 1) * dayWidth;
                                    const barLeft = startIndex * dayWidth;

                                    return (
                                        <div 
                                            key={`${sample.patientId}-${sampleIndex}`}
                                            className="samplemanager-sample-bar"
                                            style={{ 
                                                left: `${barLeft}vh`, 
                                                width: `${barWidth}vh`,
                                                height: '2vh',
                                                top: '0.5vh',
                                            }}
                                            onClick={(e) => handleSampleClick(e, sample)}
                                        >
                                            <div className="samplemanager-sample-info">
                                                <FontAwesomeIcon 
                                                    icon={kitTypes[sample.kitType] || statusIcons[sample.status.currentStage]} 
                                                    className="samplemanager-status-icon" 
                                                />
                                                <span className="samplemanager-sample-name">
                                                    {sample.firstName} {sample.lastName}
                                                </span>
                                                <span className="samplemanager-sample-progress">
                                                    {barData.progressPercentage}%
                                                </span>
                                            </div>
                                            
                                            {/* Stage markers with icons */}
                                            {/* Stage markers with icons */}
                                            {Object.entries(barData.stagesDates || {}).map(([stageName, stageDate], i) => { // stageDate is now a UTC midnight Date object
                                                // Skip if no date or if it's 'Order Received'
                                                if (!stageDate || stageName === 'Order Received') return null;

                                                // --- Robust Day Calculation ---
                                                // Get time values (milliseconds since epoch, inherently UTC)
                                                const stageDateTime = stageDate.getTime();
                                                const startDateTime = barData.startDate.getTime();

                                                // Calculate the difference in milliseconds
                                                const diffMillis = stageDateTime - startDateTime;

                                                // Define milliseconds in a standard day
                                                const millisPerDay = 1000 * 60 * 60 * 24;

                                                // Calculate days, rounding to the nearest whole day to account for DST/float issues
                                                const dayFromStart = Math.round(diffMillis / millisPerDay);
                                                // --- End Robust Day Calculation ---

                                                // Add 0.5 to center the icon in the day
                                                const iconPosition = (dayFromStart + 0.5) * dayWidth;

                                                // Only show if the icon falls within the bar width (calculate bar width in days)
                                                const barDurationDays = Math.round((barData.endDate.getTime() - barData.startDate.getTime()) / millisPerDay) + 1; // +1 because duration includes start and end day
                                                const barTotalWidth = barDurationDays * dayWidth;

                                                // Check if the center of the icon's day slot is within the bar
                                                if (iconPosition >= (0.5 * dayWidth) && iconPosition <= (barTotalWidth - (0.5 * dayWidth))) {
                                                    return (
                                                        <div
                                                            key={`stage-${i}`}
                                                            className="samplemanager-stage-marker"
                                                            style={{
                                                                left: `${iconPosition}vh`,
                                                            }}
                                                            title={`${stageName}: ${stageDate.toLocaleDateString(undefined, { timeZone: 'UTC' })} (UTC)`} // Display date clearly
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={statusIcons[stageName]}
                                                                className="samplemanager-stage-icon"
                                                            />
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            
            {showDetailCard && selectedSample && (
                <SampleDetail 
                    sample={selectedSample}
                    onClose={handleCloseDetailCard}
                    onSaveChanges={onRefreshData}
                />
            )}
        </div>
    );
};

export default Calendar;
